import React, { ChangeEvent, Component, HTMLAttributes } from "react";
import { FileItem } from "./FileItem";
import { IFileUploadProps, IFileUploadState } from "./Fileupload.types";
import { FileupLoadHeader } from "./FileupLoadHeader";
import { InputError } from "../InputError/InputError";
import { getIdFriendlyName } from "../../utils/guid";
import { InputInfo } from "../InputInfo/inputInfo";
import {
  FileExtendedDetails,
  FileState,
} from "../../commonTypes/fileupload.types";
import { FormFieldFile } from "../../commonTypes/formField.types";
import { hashCode } from "../../utils/hash";

const DEFAULT_STATE_VALUE = [] as FileExtendedDetails[];
export class Fileupload extends Component<IFileUploadProps, IFileUploadState> {
  static defaultProps = {
    disabled: true,
    heading: "Fil",
    upload: "Ladda upp",
    nofilesText: "Inga Filer",
    mimetype: "",
    fieldName: "",
    max: 1,
    useDesignSystemLayout: false,
    value: DEFAULT_STATE_VALUE,
    errorMessage: "",
    infoMessage: "",
    clientError: null,
    maxFileSize: 10485760,
    maxFileSizeErrorMessage: "Filen är för stor, den får inte vara större än ",
    showMaxFilesInfoMessage: false,
    maxFilesInfoMessage: "Du kan inte ladda upp fler än {0} filer.",
    illegalFileNameErrorMessage:
      "Du kan inte ladda upp fler filer med samma filnamn.",
    removeText: "Ta bort",
    removeFileText: "Ta bort fil",
    removeFilePopupBody: "Är du säker på att du vill ta bort den här filen?",
    abortText: "Avbryt",
    closeText: "Stäng",
    okText: "Ok",
    isTouched: false,
    id: null,
    clientOnly: false,
    clientOnlyLegitimation: false,
    submitButtonIsClicked: false,
    checkMarkHeaderText: null,
    onAdd: () => {},
    onRemove: () => {},
    onChange: () => {
      /* noop */
    },
    acceptedFileTypes: null,
    wrongFileTypeErrorMessage: "",
  } as IFileUploadProps;
  private _id: string;
  private _errorId: string;
  private fileInputRef: HTMLInputElement;
  private _latestValue: FileExtendedDetails[] | undefined;

  constructor(props: IFileUploadProps) {
    super(props);
    const myId = props.id ? "fileUpload_" + props.id : null;
    this._id = myId ? myId : hashCode(this.props.heading, "fileUpload");
    this._errorId = hashCode(this.props.heading, "error");

    const { fieldName, value } = this.props;

    if (props.value !== undefined) {
      this._latestValue = value;
    } else {
      this._latestValue = DEFAULT_STATE_VALUE;
    }

    this.state = {
      fieldName,
      value: this._latestValue,
      hasFetched: false,
      //disabled: value.length === max || disabled,
      isTouched: props.isTouched,
      errorMessage: "",
      clientInfo: "",
    };
  }

  private get _errorMessage(): string | undefined {
    let { errorMessage } = this.state;
    if (!errorMessage && this.props.errorMessage) {
      errorMessage = this.props.errorMessage;
    }

    return errorMessage;
  }

  private get _infoMessage(): string | undefined {
    let { infoMessage } = this.state;
    if (!infoMessage && this.props.infoMessage) {
      infoMessage = this.props.infoMessage;
    }

    return infoMessage;
  }
  private onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    this.setState({
      isTouched: true,
    });
  };

  componentDidUpdate(prevProps: IFileUploadProps, prevState: IFileUploadState) {
    if (!this.props.clientOnly) this.fileInputRef.value = "";

    if (prevProps.isTouched !== this.props.isTouched) {
      this.setState({
        isTouched: this.props.isTouched,
      });
    }
  }

  private fileinputOnChangeClientOnly = (files: FileList) => {
    if (files.length === 0) this.fileInputRef.value = "";

    const LatestUploadedFiles = [];

    [...files].forEach((file) => {
      LatestUploadedFiles.push(fileToFileMeta(file));
    });

    const { value } = this.props;

    if (
      LatestUploadedFiles.find((x) =>
        value.find((y) => y.OriginalFilename === x.OriginalFilename)
      )
    ) {
      this.setState({
        clientError: {
          error: null,
          errorMessage: this.props.illegalFileNameErrorMessage,
        },
        isTouched: true,
      });
      return;
    }

    const filesClone = value.slice();

    LatestUploadedFiles.forEach((fileData) => {
      filesClone.push(fileData);
    });

    this.setState({ isTouched: true, clientError: null });

    this.props.onAdd({
      ...this.state,
      value: filesClone,
      isTouched: true,
    });
  };

  isCorrectFileType(fileName: string, acceptedFileEndings: string[]) {
    if (!Array.isArray(acceptedFileEndings) || !acceptedFileEndings.length) {
      return true;
    }
    //Filename can't be empty.
    if (!fileName) return false;

    const fileEnding = fileName.substring(
      fileName.lastIndexOf(".") + 1,
      fileName.length
    );
    return acceptedFileEndings.includes(fileEnding?.toLowerCase());
  }

  private fileinputOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.props.clientOnly && !this.props.clientOnlyLegitimation)
      return this.fileinputOnChangeClientOnly(e.target.files);

    if (e.target.files.length === 0) {
      // the input onChange is called even when we "empty" it in the onUpdate in IE..
      return false;
    }
    const {
      maxFileSize,
      maxFileSizeErrorMessage,
      acceptedFileTypes,
      wrongFileTypeErrorMessage,
    } = this.props;

    let latestFile = e.target.files[e.target.files.length - 1];
    if (latestFile && latestFile.size > maxFileSize) {
      this.setState({
        clientInfo:
          maxFileSizeErrorMessage + Math.round(maxFileSize / 1048576) + " MB",
      });
      return;
    }

    if (
      latestFile &&
      acceptedFileTypes &&
      !this.isCorrectFileType(latestFile.name, acceptedFileTypes)
    ) {
      this.setState({
        clientInfo: wrongFileTypeErrorMessage,
        isTouched: true,
      });
      return;
    }

    const filesMeta = fileToFileMeta(latestFile);
    const { value } = this.props;

    if (value.find((x) => x.OriginalFilename === filesMeta.OriginalFilename)) {
      this.setState({
        clientInfo: this.props.illegalFileNameErrorMessage,
        isTouched: true,
      });
      return;
    }

    if (this.props.clientOnlyLegitimation) e.target.value = "";

    const filesClone = value.slice();
    filesClone.push(filesMeta);

    this.setState({
      isTouched: true,
      clientError: null,
      clientInfo: null,
    });
    this.props.onAdd({
      ...this.state,
      value: filesClone,
      isTouched: true,
      //clientError: null
    } as FormFieldFile);
  };

  private onRemoveFile = (file: FileExtendedDetails, index: number) => {
    const filesClone = this.props.value.slice();
    this.fileInputRef.value = "";

    const fieldName = this.props.fieldName;

    this.props.onRemove(file, fieldName, {
      ...this.state,
      value: filesClone,
      disabled: false,
      isTouched: true,
      clientError: null,
    });
  };

  private renderFileItems = (currentFiles: FileExtendedDetails[]) => {
    return currentFiles.map((f: FileExtendedDetails, i: number) => {
      const keyId = `${i}-${f.OriginalFilename}`.replace(/\s/g, "");
      return (
        <FileItem
          clientOnly={this.props.clientOnly}
          key={keyId}
          index={i}
          onRemove={this.onRemoveFile}
          fileMeta={f}
          removeText={this.props.removeText}
          removeFileText={this.props.removeFileText}
          removeFilePopupBody={this.props.removeFilePopupBody}
          abortText={this.props.abortText}
          closeText={this.props.closeText}
          okText={this.props.okText}
        />
      );
    });
  };

  private get _ariaAttributes(): HTMLAttributes<any> {
    const { errorAriaAttributes } = this.props;
    if (errorAriaAttributes) return errorAriaAttributes;
    if (this._errorMessage)
      return { "aria-invalid": true, "aria-describedby": this._errorId };
    return {};
  }

  render() {
    const {
      disabled,
      nofilesText,
      heading,
      mimetype,
      upload,
      max,
      clientError,
      mandatory,
      value,
      maxFilesInfoMessage,
      showMaxFilesInfoMessage,
      clientOnly,
      clientOnlyLegitimation,
      infoBubble,
      fieldName,
      submitButtonIsClicked,
      mandatoryString,
      checkMarkHeaderText,
    } = this.props;
    const clientErrorState = this.state.clientError;
    const errorMessage = this._errorMessage;
    const infoMessage = this._infoMessage;
    const isDisabled =
      (!clientOnly || clientOnlyLegitimation) &&
      (disabled || value.length === max);
    const multiple = max > 1 && clientOnly && !clientOnlyLegitimation;
    const fileuploadClass = isDisabled ? "fileupload disabled" : "fileupload";
    const controlsId = getIdFriendlyName(heading, "itemwrapper");
    const showErrorMsg =
      clientOnly && clientOnlyLegitimation ? false : clientOnly;
    return (
      <div className={fileuploadClass}>
        <FileupLoadHeader
          controls={controlsId}
          mandatory={mandatory}
          heading={heading}
          upload={upload}
          inputId={this._id}
          infoBubble={infoBubble}
          mandatoryString={mandatoryString}
          checkMarkHeaderText={checkMarkHeaderText}
        />
        <div className={this.props.useDesignSystemLayout ? "" : "indent"}>
          <div
            id={controlsId}
            className={`fileupload__filename-wrapper ${
              (errorMessage && (!showErrorMsg || submitButtonIsClicked)) ||
              (clientError && (!showErrorMsg || submitButtonIsClicked)) ||
              (clientErrorState && (!showErrorMsg || submitButtonIsClicked))
                ? "formelement-has-error"
                : ""
            }`}
          >
            {value.length ? (
              this.renderFileItems(value)
            ) : (
              <div className="fileupload__filename fileupload__filename--nofiles">
                {nofilesText}
              </div>
            )}
          </div>
          {errorMessage && (!showErrorMsg || submitButtonIsClicked) && (
            <InputError id={this._errorId} errorText={errorMessage} />
          )}
          {clientError && (!showErrorMsg || submitButtonIsClicked) && (
            <InputError
              id={this._errorId}
              errorText={clientError.errorMessage}
            />
          )}
          {clientErrorState && (!showErrorMsg || submitButtonIsClicked) && (
            <InputError
              id={this._errorId}
              errorText={clientErrorState.errorMessage}
            />
          )}
          {showMaxFilesInfoMessage && value.length === max && (
            <InputInfo
              infoText={maxFilesInfoMessage.replace("{0}", max.toString())}
            />
          )}
          {this.state.clientInfo && (
            <InputInfo infoText={this.state.clientInfo} />
          )}
          {infoMessage && <InputInfo infoText={infoMessage} />}
        </div>

        <input
          ref={(elem) => (this.fileInputRef = elem)}
          onChange={this.fileinputOnChange}
          disabled={isDisabled}
          hidden={true}
          tabIndex={-1}
          type="file"
          id={this._id}
          onBlur={this.onBlur}
          name={clientOnly ? fieldName : heading}
          multiple={multiple}
          accept={mimetype}
          required={mandatory}
          {...this._ariaAttributes}
        />
      </div>
    );
  }
}

export function fileToFileMeta(file: File): FileExtendedDetails {
  const slashIndex = file.name.lastIndexOf("\\");
  const cleanFilename = file.name.slice(slashIndex + 1);
  return {
    file: file,
    OriginalFilename: cleanFilename,
    status: FileState.UPLOADING,
  };
}
